import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bot, Loader2, Maximize2, MessagesSquare, Minus, Send, Sparkles, Trash2, X, ClipboardList, Wand2 } from 'lucide-react';
import { chatAppScreensAI, type AppScreensChatMessagePayload } from '../api/client';
import MarkdownMessage from './MarkdownMessage';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface AppScreensAIChatPanelProps {
  open: boolean;
  onClose: () => void;
  appId: string;
  screensId: string;
  /** Live editor state — forwarded to the API so the assistant sees current screens. */
  screens: unknown[];
  primaryLocale: string;
  targetLocales: string[];
  /**
   * Phase 2 hook: when provided, each assistant reply shows an "Apply" action that
   * calls this with the reply text. Left undefined in phase 1 (suggestions are text-only),
   * which renders the action disabled with a "coming soon" tooltip.
   */
  onApplySuggestion?: (suggestion: string) => void;
}

/**
 * Right-side drawer chat assistant for the App Screens editor. The assistant is grounded
 * in the user's current (live) screens and can analyze pasted App Store Review feedback.
 * Conversation is client-only (not persisted) in phase 1.
 */
const AppScreensAIChatPanel: React.FC<AppScreensAIChatPanelProps> = ({
  open,
  onClose,
  appId,
  screensId,
  screens,
  primaryLocale,
  targetLocales,
  onApplySuggestion,
}) => {
  const { t, i18n } = useTranslation();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [reviewText, setReviewText] = useState('');
  const [reviewMode, setReviewMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const reviewRef = useRef<HTMLTextAreaElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // Keep the latest live context in a ref so the send handler always ships current screens.
  const contextRef = useRef({ screens, primaryLocale, targetLocales });
  useEffect(() => {
    contextRef.current = { screens, primaryLocale, targetLocales };
  }, [screens, primaryLocale, targetLocales]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Signal global widgets (e.g. FeedbackWidget) to step aside while the chat drawer is open.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('appscreens:chat-open', { detail: open }));
    return () => {
      window.dispatchEvent(new CustomEvent('appscreens:chat-open', { detail: false }));
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      // Focus the input when the drawer opens.
      const id = window.setTimeout(() => inputRef.current?.focus(), 50);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open]);

  const send = useCallback(
    async (text: string, opts?: { displayAs?: string; reviewOverride?: string }) => {
      const message = text.trim();
      if (!message || loading || !appId || !screensId) return;

      const display = (opts?.displayAs ?? text).trim() || message;
      const attachedReview = (opts?.reviewOverride ?? '').trim();
      const history: AppScreensChatMessagePayload[] = messages.map(m => ({ role: m.role, content: m.content }));

      setMessages(prev => [...prev, { role: 'user', content: display }]);
      setInput('');
      setLoading(true);
      try {
        const ctx = contextRef.current;
        const { reply } = await chatAppScreensAI(appId, screensId, {
          message,
          messages: history,
          reviewText: attachedReview || undefined,
          screens: ctx.screens,
          primaryLocale: ctx.primaryLocale || undefined,
          targetLocales: ctx.targetLocales,
          responseLanguage: i18n.language || undefined,
        });
        setMessages(prev => [
          ...prev,
          { role: 'assistant', content: reply || t('appScreen.aiChatError') },
        ]);
      } catch {
        setMessages(prev => [...prev, { role: 'assistant', content: t('appScreen.aiChatError') }]);
      } finally {
        setLoading(false);
        window.setTimeout(() => inputRef.current?.focus(), 30);
      }
    },
    [appId, screensId, loading, messages, t, i18n]
  );

  // Review quick-action: analyze the pasted App Store Review feedback. The pasted text is
  // shown as the user's message; a short instruction is sent as the API message and the
  // review text is attached separately so the assistant maps each point to the screens.
  const handleSendReview = useCallback(() => {
    const text = reviewText.trim();
    if (!text || loading) return;
    void send(t('appScreen.aiChatReviewAnalyzePrompt'), { displayAs: text, reviewOverride: text });
    setReviewText('');
    setReviewMode(false);
  }, [reviewText, loading, send, t]);

  const openReviewMode = useCallback(() => {
    setReviewMode(true);
    window.setTimeout(() => reviewRef.current?.focus(), 50);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void send(input);
      }
    },
    [input, send]
  );

  if (!open) return null;

  const hasMessages = messages.length > 0;

  // Minimized: collapse to a compact floating pill at bottom-right.
  if (minimized) {
    return (
      <div className="fixed bottom-4 right-4 z-40 flex items-center gap-2 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg pl-3 pr-2 py-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-300">
          <Bot className="h-4 w-4" />
        </span>
        <button
          type="button"
          onClick={() => setMinimized(false)}
          className="text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-300"
        >
          {t('appScreen.aiChatTitle')}
        </button>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-primary-500" />}
        <button
          type="button"
          onClick={() => setMinimized(false)}
          title={t('appScreen.aiChatExpand')}
          className="inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onClose}
          title={t('common.close')}
          className="inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    // Docked, non-modal drawer: no backdrop so the screens editor behind it stays interactive.
    // Starts below the app header (h-14 = top-14) so its own header / close button is reachable.
    <aside className="fixed top-14 right-0 bottom-0 z-40 flex w-full max-w-md flex-col bg-white dark:bg-gray-900 shadow-2xl border-l border-t border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="flex shrink-0 items-start gap-3 border-b border-gray-200 dark:border-gray-700 p-4">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-300">
            <Bot className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {t('appScreen.aiChatTitle')}
            </h2>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {t('appScreen.aiChatDescription')}
            </p>
          </div>
          <div className="flex items-center gap-1">
            {hasMessages && (
              <button
                type="button"
                onClick={() => setMessages([])}
                title={t('appScreen.aiChatClear')}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setMinimized(true)}
              title={t('appScreen.aiChatMinimize')}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
            >
              <Minus className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={onClose}
              title={t('common.close')}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
          {!hasMessages && !loading && (
            <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-500 dark:text-primary-300">
                <Sparkles className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t('appScreen.aiChatEmpty')}
                </p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t('appScreen.aiChatEmptyHint')}
                </p>
              </div>
              <div className="mt-1 flex flex-col gap-2 w-full max-w-xs">
                <button
                  type="button"
                  onClick={() => void send(t('appScreen.aiChatQuickReview'))}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <Wand2 className="h-4 w-4 text-primary-500 shrink-0" />
                  {t('appScreen.aiChatQuickReview')}
                </button>
                <button
                  type="button"
                  onClick={() => void send(t('appScreen.aiChatQuickIssues'))}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <ClipboardList className="h-4 w-4 text-primary-500 shrink-0" />
                  {t('appScreen.aiChatQuickIssues')}
                </button>
                <button
                  type="button"
                  onClick={openReviewMode}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <MessagesSquare className="h-4 w-4 text-primary-500 shrink-0" />
                  {t('appScreen.aiChatReviewQuick')}
                </button>
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
              <div className={m.role === 'user' ? 'max-w-[85%]' : 'max-w-[92%] w-full'}>
                <div className="mb-1 text-[11px] font-medium text-gray-400 dark:text-gray-500 px-1">
                  {m.role === 'user' ? t('appScreen.aiChatYou') : t('appScreen.aiChatAssistant')}
                </div>
                {m.role === 'user' ? (
                  <div className="rounded-2xl rounded-tr-sm bg-primary-600 px-3.5 py-2.5 text-sm text-white whitespace-pre-wrap break-words">
                    {m.content}
                  </div>
                ) : (
                  <div className="rounded-2xl rounded-tl-sm bg-gray-100 dark:bg-gray-800 px-3.5 py-2.5 text-sm text-gray-800 dark:text-gray-100 break-words">
                    <MarkdownMessage content={m.content} />
                  </div>
                )}
                {m.role === 'assistant' && (
                  <div className="mt-1.5 px-1">
                    <button
                      type="button"
                      onClick={onApplySuggestion ? () => onApplySuggestion(m.content) : undefined}
                      disabled={!onApplySuggestion}
                      title={
                        onApplySuggestion
                          ? undefined
                          : t('appScreen.aiChatApplyComingSoon')
                      }
                      className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-primary-600 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    >
                      <Wand2 className="h-3.5 w-3.5" />
                      {t('appScreen.aiChatApply')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="inline-flex items-center gap-2 rounded-2xl rounded-tl-sm bg-gray-100 dark:bg-gray-800 px-3.5 py-2.5 text-sm text-gray-500 dark:text-gray-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('appScreen.aiChatThinking')}
              </div>
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 p-3 space-y-2">
          {reviewMode ? (
            /* Review analysis mode: paste the App Store Review comment, then analyze. */
            <div className="rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50/60 dark:bg-primary-900/20 p-2.5">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-700 dark:text-primary-300">
                  <MessagesSquare className="h-3.5 w-3.5" />
                  {t('appScreen.aiChatReviewLabel')}
                </span>
                <button
                  type="button"
                  onClick={() => setReviewMode(false)}
                  title={t('common.cancel')}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <textarea
                ref={reviewRef}
                value={reviewText}
                onChange={e => setReviewText(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    handleSendReview();
                  }
                }}
                placeholder={t('appScreen.aiChatReviewPlaceholder')}
                rows={4}
                className="w-full resize-y rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2.5 py-2 text-sm text-gray-800 dark:text-gray-100 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
              />
              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  onClick={handleSendReview}
                  disabled={!reviewText.trim() || loading}
                  className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-40 disabled:pointer-events-none"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {t('appScreen.aiChatReviewSend')}
                </button>
              </div>
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={openReviewMode}
                className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-500 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-300"
              >
                <MessagesSquare className="h-3.5 w-3.5" />
                {t('appScreen.aiChatReviewQuick')}
              </button>
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t('appScreen.aiChatPlaceholder')}
                  rows={1}
                  className="flex-1 resize-none rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3.5 py-2.5 text-sm text-gray-800 dark:text-gray-100 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400 max-h-32"
                />
                <button
                  type="button"
                  onClick={() => void send(input)}
                  disabled={!input.trim() || loading}
                  title={t('appScreen.aiChatSend')}
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40 disabled:pointer-events-none"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </div>
            </>
          )}
        </div>
    </aside>
  );
};

export default AppScreensAIChatPanel;
